import React from 'react';
import PageSection from './PageSection';
import StepNumber from './StepNumber';
import { tie } from './typography';

/**
 * Кілька коротких кроків на сірій панелі: підпис, заголовок і кроки в ряд,
 * між кружками — лінія. Нижче точки `columnsFrom` ряд стає шкалою згори
 * вниз, і лінія з горизонтальної стає вертикальною.
 *
 * Так уже були зроблені «Що буде після звернення» на контактах і «Починаємо
 * не з прайсу» на цінах — кожна сторінка своєю копією, і копії встигли
 * розійтися відступами й заголовком. Тепер це один блок.
 *
 * `columnsFrom` — з якої ширини кроки стають у ряд: 'md' для трьох
 * коротких, 'lg' для чотирьох і більше (на планшеті четверта колонка
 * лишала б по три-чотири слова в рядку).
 *
 * `highlightLast` заливає останній кружок — коли останній крок є
 * результатом, до якого ведуть решта (старт роботи).
 *
 * Розміри лінії прив'язані до кружка sm (32px): вертикальна стоїть по його
 * центру (left-4), горизонтальна — на висоті центру (top-4) і починається
 * за кружком із відступом (left-12). Тому розмір кружка тут не
 * налаштовується.
 *
 * Класи прописані повністю, а не складені з `${bp}:` — інакше Tailwind їх
 * не побачить у коді й не згенерує.
 */
const BREAKPOINTS = {
  md: {
    cols: { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' },
    list: 'md:gap-8',
    item: 'md:flex-col md:gap-5',
    vLine: 'md:hidden',
    hLine: 'hidden md:block',
    body: 'md:pt-0'
  },
  lg: {
    cols: { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' },
    list: 'lg:gap-8',
    item: 'lg:flex-col lg:gap-5',
    vLine: 'lg:hidden',
    hLine: 'hidden lg:block',
    body: 'lg:pt-0'
  }
};

const StepRow = ({
  id,
  caption,
  captionTone,
  title,
  description,
  steps = [],
  columnsFrom = 'md',
  highlightLast = false,
  spacing,
  className = ''
}) => {
  const bp = BREAKPOINTS[columnsFrom] || BREAKPOINTS.md;
  const cols = bp.cols[Math.min(Math.max(steps.length, 2), 5)];

  return (
    <PageSection
      id={id}
      caption={caption}
      captionTone={captionTone}
      title={title}
      description={description}
      spacing={spacing}
      className={`rounded-2xl bg-slate-50 border border-slate-100 p-6 sm:p-8 lg:p-10 ${className}`}
    >
      <ol className={`list-none m-0 p-0 grid gap-6 ${cols} ${bp.list}`}>
        {steps.map((step, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={step.title} className={`relative flex gap-4 ${bp.item}`}>
              {!last && (
                <>
                  <span className={`${bp.vLine} absolute left-4 top-10 -bottom-5 w-px bg-teal-200`} aria-hidden="true"></span>
                  <span className={`${bp.hLine} absolute top-4 left-12 -right-6 h-px bg-teal-200`} aria-hidden="true"></span>
                </>
              )}
              <StepNumber n={i + 1} size="sm" variant={highlightLast && last ? 'filled' : 'outline'} />
              <div className={`pt-1 ${bp.body}`}>
                <h3 className="font-semibold text-lg text-slate-900 font-display">{tie(step.title)}</h3>
                <p className="mt-1.5 leading-relaxed text-slate-600">{tie(step.text)}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </PageSection>
  );
};

export default StepRow;
